// Audio through the browser's own element, presented like a track: artwork
// where the host supplied it, a neutral tile where not, the title, and the
// controls the element lacks (speed, loop). Metadata is what the element
// reports; a waveform and equaliser are not drawn, because that would need a
// decoder this package does not carry.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Select } from '../../inputs/Select/Select.js';
import { IconButton, Notice, Toolbar, ToolbarGroup, ToolbarText } from '../chrome.js';
import { FamilyIcon, IconLoop } from '../icons.js';
import { formatDuration } from '../format.js';
import type { RendererProps } from '../types.js';

const SPEEDS = ['0.5', '0.75', '1', '1.25', '1.5', '2'];

export function AudioRenderer({ file, source, labels, register, download, openExternally }: RendererProps): ReactNode {
  const audio = useRef<HTMLAudioElement | null>(null);
  const [speed, setSpeed] = useState('1');
  const [loop, setLoop] = useState(false);
  const [duration, setDuration] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const src = file.streamUrl ?? file.objectUrl ?? '';
  const title = source.title ?? file.name.replace(/\.[^.]+$/, '');

  useEffect(() => { if (audio.current) audio.current.playbackRate = Number(speed); }, [speed]);
  useEffect(() => { if (audio.current) audio.current.loop = loop; }, [loop]);

  const onKey = useCallback((e: KeyboardEvent) => {
    const el = audio.current;
    if (!el) return false;
    if (e.key === ' ') { if (el.paused) void el.play(); else el.pause(); return true; }
    if (e.key === 'ArrowLeft') { el.currentTime = Math.max(0, el.currentTime - 10); return true; }
    if (e.key === 'ArrowRight') { el.currentTime = Math.min(el.duration || Infinity, el.currentTime + 10); return true; }
    return false;
  }, []);

  useEffect(() => {
    register({
      capabilities: { fullscreen: false },
      details: [
        ...(duration != null && Number.isFinite(duration) ? [{ label: labels.detailDuration, value: formatDuration(duration) }] : []),
        { label: labels.detailRenderer, value: labels.rendererNative }
      ],
      status: duration != null && Number.isFinite(duration) ? formatDuration(duration) : undefined,
      onKey
    });
  }, [register, duration, labels, onKey]);

  if (failed) {
    return <Notice family="audio" title={labels.unsupportedTitle} message={labels.unsupportedMediaCodec} labels={labels} download={download} openExternally={openExternally} />;
  }

  return (
    <div className="rr-fv__renderer">
      <Toolbar>
        <ToolbarGroup>
          <ToolbarText>{labels.playbackSpeed}</ToolbarText>
          <Select size="xs" aria-label={labels.playbackSpeed} value={speed} onChange={(e) => setSpeed(e.currentTarget.value)}
            data={SPEEDS.map((s) => ({ value: s, label: `${s}×` }))} />
          <IconButton label={labels.loop} pressed={loop} active={loop} onClick={() => setLoop((v) => !v)}><IconLoop /></IconButton>
        </ToolbarGroup>
      </Toolbar>
      <div className="rr-fv__canvas rr-fv__canvas--audio">
        <div className="rr-fv__audio-card">
          <div className="rr-fv__audio-art">
            {source.thumbnail
              ? <img src={source.thumbnail} alt="" />
              : <div className="rr-fv__tile rr-fv__tile--audio rr-fv__tile--big"><FamilyIcon family="audio" size={40} /></div>}
          </div>
          <div className="rr-fv__audio-body">
            <div className="rr-fv__audio-title">{title}</div>
            <div className="rr-fv__audio-sub">{file.name}{duration != null && Number.isFinite(duration) ? ` · ${formatDuration(duration)}` : ''}</div>
            <audio
              ref={audio}
              className="rr-fv__audio"
              controls
              preload="metadata"
              src={src}
              onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
              onError={() => setFailed(true)}
            >
              {labels.unsupportedMediaCodec}
            </audio>
          </div>
        </div>
      </div>
    </div>
  );
}
